import { useEffect, useCallback, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { InformationCircleIcon, TrashIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import MapItem from './MapItem.jsx';
import ModalError from '../Modal/ModalError.jsx';
import MapEditorDelete from './MapEditorDelete.jsx';
import MapEditorTabBtn from './MapEditorTabBtn.jsx';
import MapEditorPeople from './MapEditorPeople.jsx';
import MapEditorDetailsView from './MapEditorDetailsView.jsx';
import MapEditorPlotEditor from './MapEditorPlotEditor.jsx';
import MapEditorLotSearch from './MapEditorLotSearch.jsx';
import useAccessLevel from '/src/hooks/useAccessLevel';

export default function MapEditor({map, _id, onNew, onNewSave, onClose}){

    const [error, setError] = useState(null);
    const [newItem, setNewItem] = useState(null);
    const _open = newItem || _id;
    const [mode, setMode] = useState('overview');

    const canEditOccupants = useAccessLevel('visitor');
    const canEditPlots = useAccessLevel('staff');

    function close(){
        setNewItem(null);
        setError(null);
        setMode('overview');
        if(onClose) onClose();
    }

    // handle map click (open new marker for edit)
    const onMapClick = useCallback((e)=>{
        console.debug('onMapClick', e);
        const {lat, lng} = e.latlng;
        const data = {lat, lng};
        setNewItem(data);
        if(onNew) onNew(data);
    }, []);

    // attach map click (open new marker for edit)
    useEffect(()=>{
        if(map && canEditPlots){    
            map.on('click', onMapClick);
            return ()=>{
                if(map){ 
                    map.off('click', onMapClick);
                }
            }
        }
    },[map, canEditPlots]);

    // let map know width may have changed
    useEffect(()=>{
        if(map){    
            // This timeout ms must be >= the transition time for the slide out animation.
            // This can cause map.flyTo() to be interrupted, so time must also allow for it to complete.
            setTimeout(()=>map.invalidateSize(), 200);
        }
    },[map, _id, newItem]);

    // automatically open to edit mode if item is new
    useEffect(()=>{
        if(newItem) setMode('plot');
    },[newItem]);

    function closeOnEscape(e){
        if(e && e.key == 'Escape'){
            close();
        }
    }
    
    return (
        <div className={`w-96 ${_open?'max-w-sm':'max-w-0'} transition-max-w duration-200 overflow-x-hidden relative flex flex-col bg-white`} >
            {_open && (
                <div>
                    {newItem && (
                        <MapItem map={map} lat={newItem.lat} lng={newItem.lng} open={true} onClose={()=>close()} />
                    )}
                    {error && <ModalError title="Error" message={error.message} description={error.status || error.code} callback={()=>setError(null)} />}
                    
                    <div className='flex pr-4'>
                        <h3 className='text-2xl font-light m-4'>{_id?'Edit':'New'} Plot</h3>
                        <span className='flex-grow flex items-center'>
                            <MapEditorTabBtn title="Plot Details" active={mode == 'plot' || mode == 'overview'} cb={()=>setMode('overview')} ><InformationCircleIcon className='w-6' /></MapEditorTabBtn>
                            {canEditOccupants && <MapEditorTabBtn title="People & Ownership" active={mode == 'people'} cb={()=>setMode('people')} disabled={!_id} ><UserGroupIcon className='w-6' /></MapEditorTabBtn>}
                            {canEditPlots && <MapEditorTabBtn title="Delete Plot" active={mode == 'delete'} cb={()=>setMode('delete')} disabled={!_id} ><TrashIcon className='w-6' /></MapEditorTabBtn>}
                        </span>
                        <button onClick={()=>close()}><XMarkIcon className='w-8 text-gray-400' /></button>
                    </div>

                    <div className="overflow-x-hidden overflow-y-auto py-3 px-4 max-h-[85vh] border-t border-t-gray-100">
                        {((mode == 'overview') && (
                            <MapEditorDetailsView 
                                _id={_id} 
                                onEditPlot={()=>setMode('plot')} 
                                onEditPeople={(pid)=>setMode('people')} 
                            />
                        )) || ((mode == 'plot') && (
                            <div onKeyDown={closeOnEscape}>
                                {newItem && (
                                    <div className='mb-4'>
                                        <h4 className='text-2xl font-light'>Search</h4>
                                        <MapEditorLotSearch onSuccess={()=>close()} lat={newItem.lat} lng={newItem.lng} />
                                        <hr className='border-black mb-4 mt-8' />
                                    </div>
                                )}
                                <div>
                                    <h4 className='text-2xl font-light'>Manual Entry</h4>
                                    <MapEditorPlotEditor 
                                        _id={_id} 
                                        lat={newItem && newItem.lat} 
                                        lng={newItem && newItem.lng} 
                                        onError={e=>setError(e)} 
                                        onDiscard={()=>newItem?close():setMode('overview')} 
                                        onSave={(_id)=>{setMode('overview'); if(_id) onNewSave(_id);}} 
                                    />
                                </div>
                            </div>
                            
                        )) || ((mode == 'people') && (
                            <MapEditorPeople 
                                plotId={_id} 
                                onError={e=>setError(e)} 
                            />
                        )) || ((mode == 'delete') && (
                            <MapEditorDelete 
                                _id={_id} 
                                onDelete={()=>close()} 
                                onError={e=>setError(e)} 
                            />
                        )) || (
                            <p>Select a tab from the options above.</p>
                        )}
                    </div>
                    <div className='flex-grow'></div>
                    <p className='block text-ellipsis break-words font-mono text-yellow-400 py-2 px-3 absolute bottom-0 left-0 text-xs'>{_id || 'N/A'}</p>
                </div>
            )}
        </div>
    )
}